'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/contexts/AuthContext';

interface ForgotPasswordFormData {
  email: string;
}

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setErrorMsg('');
    try {
      await forgotPassword(data.email);
      setSubmitted(true);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--background)' }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-8"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2 mb-8">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'var(--primary)' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
            </svg>
          </div>
          <span className="font-bold text-lg" style={{ color: 'var(--foreground)' }}>
            AWS Club Portal
          </span>
        </div>

        {submitted ? (
          <div className="text-center py-4 fade-in">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 className="text-xl font-700 mb-2" style={{ color: 'var(--foreground)' }}>
              Check your email
            </h2>
            <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
              We&apos;ve sent a password reset link to your email address. Click the link to reset your password.
            </p>
            <a
              href="/"
              className="btn-primary px-6 py-2.5 rounded-xl text-sm font-600 inline-block"
            >
              Back to Sign In
            </a>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-700" style={{ color: 'var(--foreground)' }}>
                Forgot Password
              </h2>
              <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
                Enter your email and we&apos;ll send you a reset link.
              </p>
            </div>

            {errorMsg && (
              <div
                className="flex items-center gap-2 px-4 py-3 rounded-xl mb-4 fade-in"
                style={{ background: 'var(--negative-bg)', border: '1px solid rgba(239,68,68,0.3)' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                <p className="text-sm" style={{ color: '#ef4444' }}>{errorMsg}</p>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label
                  htmlFor="fp-email"
                  className="block text-sm font-500 mb-1.5"
                  style={{ color: 'var(--foreground)' }}
                >
                  Email Address
                </label>
                <input
                  id="fp-email"
                  type="email"
                  placeholder="you@awsclub.cse"
                  className="w-full px-4 py-2.5 rounded-xl text-sm transition-all duration-150"
                  style={{
                    background: 'var(--input)',
                    border: `1px solid ${errors.email ? 'var(--negative)' : 'var(--border)'}`,
                    color: 'var(--foreground)',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'var(--primary)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = errors.email ? 'var(--negative)' : 'var(--border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email' },
                  })}
                />
                {errors.email && (
                  <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>
                    {errors.email.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full py-3 rounded-xl text-sm font-600 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ minHeight: '44px' }}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>

            <p className="text-sm text-center mt-5" style={{ color: 'var(--muted-foreground)' }}>
              Remember your password?{' '}
              <a
                href="/"
                className="font-600 transition-colors hover:underline"
                style={{ color: 'var(--secondary-foreground)' }}
              >
                Sign in
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
